import ReactDOM from 'react-dom/client';
import toast, { Toaster } from 'react-hot-toast';
import { BrowserRouter as Router } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { UserbackProvider } from '@userback/react';
import { AxiosError } from 'axios';
import { theme } from 'common/theme/theme';
import { BackendErrorResponse } from 'common/types/common';
import * as Sentry from '@sentry/react';
import { DemoContext } from 'common/ui/containers/demo-context';
import { VideoAppContext } from 'common/ui/containers/video-app-context';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AppTypeProvider } from 'common/constants/routes/app-type-context';
import { initializeAppType } from 'common/utils/app-type';
import { App } from './app';
import { InternalAuthProvider } from '../../common/auth';
import { BackendErrorToast, RuntimeError } from '../../common/errors';

import '../../config/i18n';
import '../../config/sentry';

initializeAppType('advon');

const root = ReactDOM.createRoot(document.getElementById('root'));

const handleReqError = (error: unknown) => {
  if (error instanceof AxiosError) {
    const backendError = error as AxiosError<BackendErrorResponse>;
    backendError.response.data?.errors?.forEach(error => {
      toast.error(<BackendErrorToast error={error} />, { duration: 10000 });
    });
  }
};

const updateAppInformation = () => {
  document.title = 'Content Creator UI';
  // Set favicon dynamically
  const faviconUrl = '/favicons/favicon.svg';
  let link = document.querySelector('link[rel~="icon"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link') as HTMLLinkElement;
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.type = 'image/png';
  link.href = faviconUrl;

  // Set meta description dynamically
  const descriptionContent = 'Content Creator UI';
  let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
  if (!metaDesc) {
    metaDesc = document.createElement('meta');
    metaDesc.name = 'description';
    document.head.appendChild(metaDesc);
  }
  metaDesc.content = descriptionContent;
};

function InitAdvonApp() {
  updateAppInformation();

  const isProduction = !window.location.hostname.match(/^(localhost|127\.|192\.168\.|0\.0\.0\.0)/);
  const shouldLoadUserback = isProduction && !!process.env.REACT_APP_USERBACK_KEY;

  const appContent = (
    <ThemeProvider theme={theme}>
      <Auth0Provider
        domain={process.env.REACT_APP_AUTH0_DOMAIN}
        clientId={process.env.REACT_APP_AUTH0_CLIENT_ID}
        authorizationParams={{
          redirect_uri: window.location.origin,
          audience: 'https://advon.ai',
        }}
        cacheLocation="localstorage"
      >
        <Toaster toastOptions={{ style: { maxWidth: '30vw', paddingRight: 0 } }} />
        <QueryClientProvider
          client={
            new QueryClient({
              defaultOptions: {
                queries: { refetchOnWindowFocus: false, staleTime: 3 * (60 * 1000), cacheTime: 5 * (60 * 1000) },
                mutations: { onError: error => handleReqError(error) },
              },
            })
          }
        >
          {process.env.NODE_ENV === 'development' && <ReactQueryDevtools />}
          <Router>
            <AppTypeProvider>
              <InternalAuthProvider>
                <DemoContext>
                  <VideoAppContext>
                    <App />
                  </VideoAppContext>
                </DemoContext>
              </InternalAuthProvider>
            </AppTypeProvider>
          </Router>
        </QueryClientProvider>
      </Auth0Provider>
    </ThemeProvider>
  );

  root.render(
    <Sentry.ErrorBoundary fallback={<RuntimeError />}>
      {shouldLoadUserback ? (
        <UserbackProvider token={process.env.REACT_APP_USERBACK_KEY}>{appContent}</UserbackProvider>
      ) : (
        appContent
      )}
    </Sentry.ErrorBoundary>
  );
}

InitAdvonApp();
